/**
 * m3 echo Frontend - Autopilot query keys and list hook
 *
 * The dashboard's autopilot indicators were the last read still on a bare
 * literal key. `['autopilots']` is the same key for every account and every
 * tenant, so a switch without a reload could paint the previous tenant's
 * autopilot badges onto the new tenant's project rows until the refetch landed.
 *
 * The factory mirrors `projectKeys` and `jobKeys`: `all` stays unscoped so a
 * broad invalidation keeps matching every scope, and everything below it
 * carries the identity.
 */

import { useQuery } from '@tanstack/react-query';
import { listAutopilots } from '../services/autopilots';
import { getIdentityScope } from '../stores/useAuthStore';

export const autopilotKeys = {
  all: ['autopilots'] as const,
  scoped: () => [...autopilotKeys.all, getIdentityScope()] as const,
  lists: () => [...autopilotKeys.scoped(), 'list'] as const,
  list: (filters?: unknown) => [...autopilotKeys.lists(), { filters }] as const,
};

type AutopilotFilters = Parameters<typeof listAutopilots>[0];

/**
 * Every autopilot the current identity may see, optionally narrowed.
 */
export const useAutopilots = (filters?: AutopilotFilters) =>
  useQuery({
    queryKey: autopilotKeys.list(filters),
    queryFn: () => listAutopilots(filters),
  });
